import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { upcomingEvents, pastEvents } from "../data/eventsData";
import { useI18n } from "../data/i18n";
import Reveal from "../components/Reveal";

const DiamondDivider = () => (
  <div className="flex items-center justify-center gap-2 my-4">
    <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#e4c590]"></div>
    <span className="diamond-mark"></span>
    <span className="diamond-mark opacity-60"></span>
    <span className="diamond-mark"></span>
    <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#e4c590]"></div>
  </div>
);

// Оптимизиран EventCard с по-бързо зареждане на изображения
const EventCard = ({ event, isUpcoming, index }) => {
  const { t, lang } = useI18n();
  const title = lang === "bg" ? event.titleBg : event.titleEn;
  const date = lang === "bg" ? event.dateBg : event.dateEn;
  const short = lang === "bg" ? event.shortBg : event.shortEn;
  
  // За по-бързо зареждане - използваме по-малка забава
  const delay = Math.min(index * 50, 200); // Максимум 200ms забава
  
  return (
    <Reveal direction="up" delay={delay} className="h-full">
      <Link
        to={`/events/${event.id}`}
        className="event-card group h-full flex flex-col"
      >
        <div className="event-card-image-wrap relative overflow-hidden">
          {/* Използваме native lazy loading + по-бързо зареждане */}
          <img
            src={event.image}
            alt={title}
            className="event-card-image w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            decoding="async" // Добавяме за по-бързо декодиране
          />
          {event.badge && <span className="event-card-badge">{event.badge}</span>}
          <div className="event-card-overlay absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
              <span className="event-card-cta opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-[#e4c590] flex items-center gap-2">
                {t("events.see_more")} <ArrowRight size={14} />
              </span>
          </div>
        </div>
        <div className="event-card-body p-6 flex-grow">
          <div className="flex items-center gap-2 text-[#e4c590] text-xs uppercase tracking-widest mb-3">
            <Calendar size={14} />
            <span>{date}</span>
          </div>
          <h3 className="display text-2xl md:text-3xl text-[#f5f0e6] group-hover:text-[#e4c590] transition-colors mb-2">{title}</h3>
          {short && <p className="text-[#a8a297] text-sm leading-relaxed">{short}</p>}
        </div>
      </Link>
    </Reveal>
  );
};

// Компонент за виртуализиран списък (зарежда само видимите елементи)
const EventList = ({ events, isUpcoming, title }) => {
  const { t } = useI18n();
  const containerRef = useRef(null);
  const [visibleItems, setVisibleItems] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    // Зареждаме веднага първите 6 елемента
    const initialLoad = events.slice(0, 6);
    setVisibleItems(initialLoad);
    setHasLoaded(true);

    // След това зареждаме останалите на части
    if (events.length > 6) {
      const timer = setTimeout(() => {
        const remaining = events.slice(6);
        // Зареждаме на групи от по 3
        let loaded = 6;
        const interval = setInterval(() => {
          const nextBatch = events.slice(loaded, loaded + 3);
          if (nextBatch.length === 0) {
            clearInterval(interval);
            return;
          }
          setVisibleItems(prev => [...prev, ...nextBatch]);
          loaded += nextBatch.length;
        }, 100); // Малко забава между зарежданията
        return () => clearInterval(interval);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [events]);

  // Ако няма събития
  if (events.length === 0) {
    return <p className="text-[#a8a297]">{t("events.no_past")}</p>;
  }

  return (
    <div ref={containerRef}>
      <Reveal direction="up" delay={50}>
        <div className="flex items-center gap-4 mb-8">
          <h2 className="display text-3xl md:text-4xl text-[#e4c590]">{title}</h2>
          <div className="flex-1 h-px bg-[#1f1f1f]"></div>
        </div>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {visibleItems.map((event, idx) => (
          <EventCard key={event.id} event={event} isUpcoming={isUpcoming} index={idx} />
        ))}
      </div>
    </div>
  );
};

const Events = () => {
  const { t } = useI18n();
  const upcoming = upcomingEvents();
  const past = pastEvents();

  return (
    <div className="bg-[#0a0a0a] pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header - без забава */}
        <div className="text-center mb-16">
          <Reveal direction="up" delay={0}>
            <p className="section-label">{t("events.label")}</p>
          </Reveal>
          <Reveal direction="up" delay={50}>
            <h1 className="display text-5xl md:text-7xl text-[#f5f0e6] mt-3">{t("events.title")}</h1>
          </Reveal>
          <Reveal direction="fade" delay={50}>
            <DiamondDivider />
          </Reveal>
          <Reveal direction="up" delay={50}>
            <p className="text-[#cfc7b8] italic max-w-2xl mx-auto">{t("events.subtitle")}</p>
          </Reveal>
        </div>

        {/* Upcoming Events - зареждат се бързо */}
        <div className="mb-20">
          <EventList 
            events={upcoming} 
            isUpcoming={true} 
            title={t("events.upcoming")} 
          />
        </div>

        {/* Past Events - зареждат се след това */}
        <div>
          <EventList 
            events={past} 
            isUpcoming={false} 
            title={t("events.past")} 
          />
        </div>
      </div>
    </div>
  );
};

export default Events;